export function getGuideBySlug(slug) {
  if (!slug || typeof slug !== 'string') return undefined;
  const key = slug.toLowerCase();
  return GUIDE_ARTICLES.find((g) => g.slug === key);
}

export function getAllGuideSlugs() {
  return GUIDE_ARTICLES.map((g) => g.slug);
}

export const GUIDE_ARTICLES = [
  {
    slug: 'biggest-pitfalls-in-seo',
    title: 'The biggest pitfalls in SEO (and how to avoid them)',
    description:
      'Thin content, ignored technical basics, chasing vanity keywords, and neglecting measurement — common SEO mistakes UK businesses make and what to do instead.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Expecting results without clear intent',
        paragraphs: [
          'Many sites chase high-volume keywords that do not match what the business actually sells or serves. Traffic that does not convert wastes crawl budget and skews your sense of progress.',
          'Start from customer questions and commercial intent: what would someone type when they are ready to enquire? Align pages to those queries and support them with proof, service detail, and obvious next steps.'
        ]
      },
      {
        h2: 'Ignoring technical foundations',
        paragraphs: [
          'Slow pages, broken links, poor mobile layout, missing or duplicate titles, and weak internal linking all cap how well even good content can perform.',
          'You do not need every advanced tactic on day one — but HTTPS, sensible URL structure, indexable pages, and a clean XML sitemap are baseline expectations in competitive markets.'
        ]
      },
      {
        h2: 'Publishing once and never iterating',
        paragraphs: [
          'Search is competitive; “set and forget” rarely wins. Outdated stats, stale offers, and pages that never earn new internal links tend to drift downward.',
          'Plan modest, regular updates: refresh key facts, add FAQs from real enquiries, and tighten pages that already get impressions but underperform on clicks.'
        ]
      }
    ]
  },
  {
    slug: 'what-is-aeo-and-why-it-matters',
    title: 'What is AEO (Answer Engine Optimisation) and why does it matter?',
    description:
      'How answer engines, AI overviews, and conversational search change what “ranking” means — and how to structure content for both Google and generative results.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'From ten blue links to direct answers',
        paragraphs: [
          'Users increasingly get summaries and direct answers before they click. AEO is about making your content easy to quote accurately: clear headings, concise definitions, and factual statements a system can extract without garbling your brand.',
          'That does not replace classic SEO; it adds another lens. Pages that are vague, contradictory, or buried in fluff are less likely to be surfaced as a trusted answer.'
        ]
      },
      {
        h2: 'Structure beats keyword stuffing',
        paragraphs: [
          'Use logical H2/H3 hierarchy, answer the main question in plain language early, then add depth. Lists, tables, and step-by-step flows often map well to how answers are assembled.',
          'Keep brand, location, and service boundaries accurate — mis-cited details are a reputational risk when content is reused in AI summaries.'
        ]
      },
      {
        h2: 'Why it matters for local and B2B sites',
        paragraphs: [
          'If you serve a geography or niche, explicit service areas, credentials, and contact paths help both traditional results and answer-style responses point users to you rather than a generic competitor.',
          'Measurement shifts: track assisted conversions and branded searches, not only a single keyword position.'
        ]
      }
    ]
  },
  {
    slug: 'custom-development-vs-drag-and-drop-long-term',
    title: 'Is custom development the best long-term plan, or will drag-and-drop see my business through?',
    description:
      'When website builders are enough, when bespoke code wins, and how to decide without marketing hype — especially for growing UK SMEs.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'When drag-and-drop is genuinely enough',
        paragraphs: [
          'Visual builders shine for brochure sites, simple bookings, and teams that want to tweak copy weekly without a developer. If your roadmap stays modest and you accept template constraints, total cost of ownership can stay predictable.',
          'Watch for plugin sprawl, performance ceilings, and migration pain if you later need a very custom data model or integration.'
        ]
      },
      {
        h2: 'When custom (or hybrid) development earns its place',
        paragraphs: [
          'Custom stacks fit non-standard journeys: multi-step quoting, complex inventory, deep CRM or ERP integration, or performance budgets that templates struggle to meet.',
          'Long term, the question is not “which label sounds premium” but whether you can evolve the product without fighting the platform every quarter.'
        ]
      },
      {
        h2: 'A practical decision frame',
        paragraphs: [
          'List must-have user journeys for the next 18–24 months, including SEO and measurement. If the builder can model them cleanly with room to grow, stay. If you are already bending workarounds, plan a structured migration rather than patching forever.',
          'WebCodeCraft often helps teams who want bespoke delivery with predictable monthly care — compare packages on our services page and ask candidly about fit.'
        ]
      }
    ]
  },
  {
    slug: 'why-does-seo-matter',
    title: 'Why does SEO matter for your business website?',
    description:
      'How organic search connects to revenue, trust, and sustainable acquisition — without confusing tactics with outcomes.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Demand already exists in Google',
        paragraphs: [
          'People search with intent — to compare, to find a local provider, to solve a problem. SEO is how you show up in those moments without paying for every click.',
          'It compounds: pages that earn relevance and links can keep producing leads while you improve other channels.'
        ]
      },
      {
        h2: 'Trust and credibility',
        paragraphs: [
          'Ranking well for relevant queries signals authority, especially alongside reviews, clear policies, and fast, accessible pages. Poor experiences erode trust even if you rank briefly.',
          'SEO is not “tricks”; it is aligning your site with what users and search systems reward: clarity, speed, usefulness, and safety.'
        ]
      },
      {
        h2: 'Balancing SEO with everything else',
        paragraphs: [
          'SEO supports brand, paid media, and referrals — it rarely replaces them. Use Search Console and analytics to see which pages pull their weight and where content or technical fixes will move the needle fastest.'
        ]
      }
    ]
  },
  {
    slug: 'what-is-accessibility-and-is-it-law',
    title: 'What is web accessibility — and is it law in the UK?',
    description:
      'A plain-English overview of digital accessibility, WCAG, UK public-sector duties, and why inclusive design helps SEO and conversions.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'What accessibility means on the web',
        paragraphs: [
          'Accessibility ensures people using keyboards, screen readers, voice control, or adjusted contrast can use your site without unnecessary barriers. It overlaps with good UX: predictable navigation, readable text, and forms that do not trap users.',
          'Standards such as WCAG describe testable success criteria; many UK organisations aim for Level AA as a pragmatic target.'
        ]
      },
      {
        h2: 'Legal context (not legal advice)',
        paragraphs: [
          'UK public-sector websites and apps have specific accessibility regulations and reporting duties. Private businesses are still subject to broader equality and consumer frameworks; requirements vary by sector and context.',
          'Treat this article as general information — obtain professional advice for your situation, especially if you operate in regulated industries.'
        ]
      },
      {
        h2: 'Why it still matters commercially',
        paragraphs: [
          'Inclusive sites reach more customers, reduce support friction, and tend to align with technical SEO basics (semantic HTML, sensible headings, working controls).',
          'You can run our free accessibility scan as a starting point, then prioritise fixes by impact and effort.'
        ]
      }
    ]
  },
  {
    slug: 'core-web-vitals-explained',
    title: 'Core Web Vitals explained for business owners',
    description:
      'What LCP, INP, and CLS measure, why Google cares about experience signals, and what to ask your developer or agency to improve.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'The three headline metrics',
        paragraphs: [
          'Largest Contentful Paint (LCP) reflects perceived load speed for main content. Interaction to Next Paint (INP) captures responsiveness to clicks and taps. Cumulative Layout Shift (CLS) tracks visual stability as the page renders.',
          'They are not the whole story of UX, but they are useful guardrails that correlate with frustration or confidence.'
        ]
      },
      {
        h2: 'Why they overlap with SEO',
        paragraphs: [
          'Search systems want to send users to pages that load and respond reliably, especially on mobile networks. Chronic performance issues can cap rankings even when copy is strong.',
          'Fixes often involve image discipline, font loading, deferring non-critical scripts, and server or CDN configuration — not only “more keywords”.'
        ]
      },
      {
        h2: 'What to request in a brief',
        paragraphs: [
          'Ask for field data where possible (Search Console, RUM), not only one-off lab scores. Set budgets for key templates: home, service, and conversion pages.',
          'Pair performance work with content priorities so you are not optimising pages users never reach.'
        ]
      }
    ]
  },
  {
    slug: 'local-seo-for-small-business-uk',
    title: 'Local SEO for small businesses in the UK',
    description:
      'Practical focus areas for appearing in local results: relevance, prominence, and clarity of service area — without spammy tactics.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Clarity beats keyword repetition',
        paragraphs: [
          'State what you do, where you operate, and how to contact you — consistently across your site, profiles, and citations. Mismatched names, phones, or addresses create doubt for users and systems alike.',
          'Dedicated location or service pages work when they add real detail, not thin duplicates for every postcode.'
        ]
      },
      {
        h2: 'Reviews and reputation',
        paragraphs: [
          'Earned reviews on reputable platforms support trust. Request them ethically after successful work; never buy fake signals.',
          'Respond to feedback where appropriate — it shows active service and can surface concerns before they cost revenue.'
        ]
      },
      {
        h2: 'Tie local SEO to the real world',
        paragraphs: [
          'Events, partnerships, sponsorships, and local links can all reinforce relevance if they are genuine. Search for sustainable visibility, not overnight tricks.'
        ]
      }
    ]
  },
  {
    slug: 'technical-seo-vs-content-seo',
    title: 'Technical SEO vs content SEO: what is the difference?',
    description:
      'How crawlability, indexing, and site health relate to on-page copy and topics — and why you need both in a serious search strategy.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Technical SEO: can we crawl and index the right pages?',
        paragraphs: [
          'This covers sitemaps, robots directives, status codes, canonical tags, structured data validity, hreflang where relevant, site speed, and internal linking architecture.',
          'If search engines cannot reliably discover or understand your priority URLs, content investments underperform.'
        ]
      },
      {
        h2: 'Content SEO: do we deserve the query?',
        paragraphs: [
          'This is topical coverage, depth, originality, intent match, and on-page structure — titles, headings, snippets people want to click, and helpful media.',
          'Great content on a broken or slow site struggles; perfect technicals with thin pages also fail.'
        ]
      },
      {
        h2: 'How teams should collaborate',
        paragraphs: [
          'Use Search Console to split issues: coverage and experience problems vs queries with impressions but weak engagement. Tackle technical blockers first when they cap entire sections of the site.',
          'Our SEO audit tool can help surface technical and on-page opportunities in one pass.'
        ]
      }
    ]
  },
  {
    slug: 'internal-linking-and-site-structure',
    title: 'Internal linking and site structure for SEO',
    description:
      'Why hierarchy, breadcrumbs, and contextual links matter for users and crawlers — and how to avoid orphan pages as you grow.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Structure is a communication tool',
        paragraphs: [
          'A logical hierarchy helps visitors orient quickly and helps search systems infer which pages are cornerstone resources versus supporting detail.',
          'Important pages should sit within a few clicks of the home page and receive contextual links from related content.'
        ]
      },
      {
        h2: 'Anchor text and context',
        paragraphs: [
          'Descriptive anchors beat “click here” spam. Link where it helps a reader naturally continue their journey — related services, deeper guides, or proof.',
          'Avoid automated footers stuffed with keyword links; keep patterns human and useful.'
        ]
      },
      {
        h2: 'Orphan pages and large sites',
        paragraphs: [
          'As catalogues grow, pages with no internal links may be discovered slowly or ignored. Periodic audits, sitemaps, and hub pages (like this guides section) reduce that risk.'
        ]
      }
    ]
  },
  {
    slug: 'measuring-seo-success-beyond-rankings',
    title: 'Measuring SEO success beyond keyword rankings',
    description:
      'KPIs that matter: qualified traffic, conversions, engagement, and brand search — with a sane reporting cadence for SMEs.',
    datePublished: '2026-04-06',
    sections: [
      {
        h2: 'Rankings are outputs, not goals',
        paragraphs: [
          'Position fluctuates by device, location, and personalisation. Tie SEO to business metrics: enquiries, revenue influenced, margin on acquired leads, and cost per acquisition versus paid channels.',
          'Segment organic traffic by landing page and intent to see what truly moves the needle.'
        ]
      },
      {
        h2: 'Engagement and quality signals',
        paragraphs: [
          'Time on page, scroll depth, and form starts are imperfect but useful when interpreted alongside context. A short time on a contact-success page may be fine; a short time on a pricing page may not.',
          'Improvement often comes from clearer headings, stronger CTAs, and faster loads — not only “more blogs”.'
        ]
      },
      {
        h2: 'Reporting cadence',
        paragraphs: [
          'Monthly reviews work for many SMEs: Search Console coverage, top queries, landing page performance, and a short list of experiments. Quarterly deeper dives for content and technical roadmaps.',
          'Avoid daily panics over single-keyword moves; watch trends and cohorts instead.'
        ]
      }
    ]
  }
];
